import React, { useState, useEffect, useContext } from 'react';
import { css, StyleSheet } from 'aphrodite/no-important';
import { clone } from 'ramda';
import { TextInputWithLabel, Button, DatePicker } from 'quantux-ui';

import CaseScheduleContext from '../case-schedule/case-schedule-context';
import { BASE_URL, padZero } from '../../utils';
import { blue, gray, salmon } from '../../theme';
import PatientCard from './patient-card';

const TODAY = new Date();
const NUMS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];

const AddPatient = (props) => {
  const [canSearchPatients, setCanSearchPatients] = useState(false);
  const [patientSearchResults, setPatientSearchResults] = useState([]);
  // --
  const [patientFirstName, setPatientFirstName] = useState('');
  const [patientMiddleName, setPatientMiddleName] = useState('');
  const [patientLastName, setPatientLastName] = useState('');
  // --
  const [ssn1, setSSN1] = useState('');
  const [ssn2, setSSN2] = useState('');
  const [ssn3, setSSN3] = useState('');
  // --
  const [gender, setGender] = useState('');
  const [dobDate, setDobDate] = useState(TODAY.getDate());
  const [dobMonth, setDobMonth] = useState(TODAY.getMonth());
  const [dobYear, setDobYear] = useState(TODAY.getFullYear());
  const [isDobDatePristine, setIsDobDatePristine] = useState(true);

  let readOnlyMode = false;
  const context = useContext(CaseScheduleContext);
  if (context) {
    readOnlyMode = context.readOnlyMode;
  }

  useEffect(() => {
    setCanSearchPatients(patientLastName.length > 0);
  }, [
    patientLastName
  ]);

  // ------------------------- STYLES -------------------------
  const styles = StyleSheet.create({
    addPatientContainer: {
      width: '412px'
    },
    sectionHeading: {
      color: blue[200],
      fontSize: '14px',
      paddingLeft: '2px'
    },
    ssnSeparator: {
      padding: '15px 8px 0 8px'
    },
    pressedBtnsContainer: {
      display: 'flex',
      padding: '2px',
      border: `1px solid ${gray[400]}`,
      borderRadius: '5px'
    },
    pressedBtn: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '14px',
      background: gray[400],
      color: salmon[500],
      height: '24px',
      width: '50px',
      borderRadius: '5px',
      cursor: 'pointer'
    },
    activePressedBtn: {
      color: 'white',
      background: blue[500]
    }
  });

  // ------------------------- EVENT HANDLERS -------------------------

  const handleOnEditPatientFirstName = (e) => {
    const { value } = e.target;
    setPatientFirstName(value);
  };

  const handleOnEditPatientMiddleName = (e) => {
    const { value } = e.target;
    setPatientMiddleName(value);
  };

  const handleOnEditPatientLastName = (e) => {
    const { value } = e.target;
    setPatientLastName(value);
  };

  const handleOnEditSSN1 = (e) => {
    const { value } = e.target;
    let val = '';
    [...value].forEach(el => {
      if (NUMS.includes(+el)) {
        val += el;
      }
    });

    if (!isNaN(val)) {
      if (val.length > 3) {
        setSSN1(val.substring(0, 3));
      } else {
        setSSN1(val);
      }
    } else {
      setSSN1('');
    }
  };

  const handleOnEditSSN2 = (e) => {
    const { value } = e.target;
    let val = '';
    [...value].forEach(el => {
      if (NUMS.includes(+el)) {
        val += el;
      }
    });
    if (!isNaN(val)) {
      if (val.length > 2) {
        setSSN2(val.substring(0, 2));
      } else {
        setSSN2(val);
      }
    } else {
      setSSN2('');
    }
  };

  const handleOnEditSSN3 = (e) => {
    const { value } = e.target;
    let val = '';
    [...value].forEach(el => {
      if (NUMS.includes(+el)) {
        val += el;
      }
    });
    if (!isNaN(val)) {
      if (val.length > 4) {
        setSSN3(val.substring(0, 4));
      } else {
        setSSN3(val);
      }
    } else {
      setSSN3('');
    }
  };

  const handleOnChangeGender = (val) => {
    setGender(val);
  };

  const handleOnClickSearchPatients = () => {
    const formData = new FormData();
    formData.append('firstName', patientFirstName);
    formData.append('middleName', patientMiddleName);
    formData.append('lastName', patientLastName);
    formData.append('ssn3', ssn3);

    if (!isDobDatePristine) {
      formData.append('dob', `${dobYear}-${padZero(dobMonth + 1)}-${padZero(dobDate)}` + 'T00:00:00.000Z');
    }

    const requestOptions = {
      method: 'POST',
      body: formData,
      redirect: 'follow'
      // headers: reqHeaders
    };

    fetch(`${BASE_URL}/patients/searchPatients`, requestOptions)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        console.log(res);
        if (res.success) {
          console.log(res.patientsList);
          setPatientSearchResults(res.patientsList.concat({
            _id: Math.random().toString(36).slice(2),
            firstName: patientFirstName,
            middleName: patientMiddleName,
            lastName: patientLastName,
            dob: isDobDatePristine ? '' : new Date(dobYear, dobMonth, dobDate).toISOString(),
            ssn1,
            ssn2,
            ssn3,
            gender,
            isTempPatient: true
          }));
        } else {
          console.log('Patient search failed!');
        }
      })
      .catch(error => console.log('error', error));
  };

  const handleOnClickAddOrCreatePatient = (patientId, isTempPatient, isNewPatient = false, newPatientData) => {
    if (isTempPatient) {
      const formData = new FormData();
      formData.append('firstName', patientFirstName);
      formData.append('middleName', patientMiddleName);
      formData.append('lastName', patientLastName);
      formData.append('ssn1', ssn1.replaceAll('?', ''));
      formData.append('ssn2', ssn2.replaceAll('?', ''));
      formData.append('ssn3', ssn3.replaceAll('?', ''));
      formData.append('gender', gender);
      formData.append('dob', `${dobYear}-${padZero(dobMonth + 1)}-${padZero(dobDate)}` + 'T00:00:00.000Z');

      const requestOptions = {
        method: 'POST',
        body: formData,
        redirect: 'follow'
        // headers: reqHeaders
      };

      let url = `${BASE_URL}/patients/createPatient`;
      if (props.isCaseSchedule) {
        url = `${BASE_URL}/patients/addPatientToCaseSchedule`;
      }

      fetch(url, requestOptions)
        .then(response => response.text())
        .then(result => {
          const res = JSON.parse(result);
          console.log(res);
          if (res.success) {
            handleOnClickAddOrCreatePatient(res.newPatient._id, false, true, res.newPatient);
          } else {
            console.error('Failed to create patient profile!');
          }
        })
        .catch(error => console.log('error', error));
    } else {
      const formData = new FormData();
      formData.append('caseId', props.caseData._id);
      formData.append('patientId', patientId);

      const requestOptions = {
        method: 'POST',
        body: formData,
        redirect: 'follow'
        // headers: reqHeaders
      };

      let url = `${BASE_URL}/patients/addPatientToCase`;
      if (props.isCaseSchedule) {
        url = `${BASE_URL}/patients/addPatientToCaseSchedule`;
      }

      fetch(url, requestOptions)
        .then(response => response.text())
        .then(result => {
          const res = JSON.parse(result);
          console.log(res);
          if (res.success) {
            if (!isNewPatient) {
              const tmp = clone(patientSearchResults.filter(el => el._id === patientId));
              console.log('+++++++++++++++++++++++++');
              console.log(tmp);
              console.log('+++++++++++++++++++++++++');
              props.onAddPatient(tmp[0]);
            } else {
              props.onAddPatient(newPatientData);
            }
          } else {
            console.log('Failed to add patient!');
          }
        })
        .catch(error => console.log('error', error));
    }
  };

  return (
    <div className={`${props.className} ${css(styles.addPatientContainer)}`}>
      {/* First, Middle and Last name */}
      <div className='pr-2 fx mt-1'>
        {/* First Name */}
        <div>
          <TextInputWithLabel
            isDisabled={readOnlyMode}
            value={patientFirstName}
            titleText="First Name"
            titleFontSize={12}
            isCompactLayout={true}
            titleColor={gray[200]}
            textColor="white"
            selectionColor="white"
            selectionBackground="#0080FF"
            spellcheck={false}
            width={113}
            onChange={handleOnEditPatientFirstName}
          />
        </div>
        {/* Middle Name */}
        <div className='ml-2'>
          <TextInputWithLabel
            isDisabled={readOnlyMode}
            value={patientMiddleName}
            titleText="Middle Name"
            titleFontSize={12}
            isCompactLayout={true}
            titleColor={gray[200]}
            textColor="white"
            selectionColor="white"
            selectionBackground="#0080FF"
            spellcheck={false}
            width={113}
            onChange={handleOnEditPatientMiddleName}
          />
        </div>
        {/* Last Name */}
        <div className='ml-2'>
          <TextInputWithLabel
            isDisabled={readOnlyMode}
            value={patientLastName}
            titleText="Last Name"
            titleFontSize={12}
            isCompactLayout={true}
            titleColor={gray[200]}
            textColor="white"
            selectionColor="white"
            selectionBackground="#0080FF"
            spellcheck={false}
            width={120}
            placeholder='Required'
            onChange={handleOnEditPatientLastName}
          />
        </div>
      </div>
      {/* SSN & DOB */}
      <div className='mt-4'>
        <div className={css(styles.sectionHeading)}>
          SS#
        </div>
        <div
          className='fx'
          style={{
            alignItems: 'flex-end',
            marginTop: '-10px',
            paddingLeft: '2px'
          }}
        >
          <div className='fx'>
            <TextInputWithLabel
              isDisabled={readOnlyMode}
              value={ssn1}
              titleText=''
              placeholderColor={gray[200]}
              placeholder='???'
              titleFontSize={12}
              isCompactLayout={false}
              titleColor={gray[200]}
              textColor="white"
              selectionColor="white"
              selectionBackground="#0080FF"
              spellcheck={false}
              width={30}
              onChange={handleOnEditSSN1}
            />
            <div className={css(styles.ssnSeparator)}>-</div>
            <TextInputWithLabel
              isDisabled={readOnlyMode}
              value={ssn2}
              titleText=''
              placeholderColor={gray[200]}
              placeholder='??'
              titleFontSize={12}
              isCompactLayout={true}
              titleColor={gray[200]}
              textColor="white"
              selectionColor="white"
              selectionBackground="#0080FF"
              spellcheck={false}
              width={26}
              onChange={handleOnEditSSN2}
            />
            <div className={css(styles.ssnSeparator)}>-</div>
            <TextInputWithLabel
              isDisabled={readOnlyMode}
              value={ssn3}
              titleText=''
              placeholderColor={gray[200]}
              placeholder='????'
              titleFontSize={12}
              isCompactLayout={true}
              titleColor={gray[200]}
              textColor="white"
              selectionColor="white"
              selectionBackground="#0080FF"
              spellcheck={false}
              width={40}
              onChange={handleOnEditSSN3}
            />
          </div>
          {/* DOB */}
          <div
            className='ml-3 fx'
            style={{ alignItems: 'baseline' }}
          >
            <div className={`pr-2 ${css(styles.sectionHeading)}`}>
              <i className='fa fa-calendar mr-2' />
              Date of Birth
            </div>
            <DatePicker
              readOnlyMode={readOnlyMode}
              left={0}
              right={-10}
              date={dobDate}
              month={dobMonth}
              year={dobYear}
              isPrisitine={isDobDatePristine}
              color={blue[400]}
              noUnderline
              onChangeDate={(year, month, date) => {
                // setShowDobAlert(false);
                setDobDate(date);
                setDobMonth(month);
                setDobYear(year);
                setIsDobDatePristine(false);
              }}
            />
          </div>
        </div>
      </div>
      {/* Biological Gender */}
      <div
        className='fx mt-4'
        style={{ alignItems: 'center' }}
      >
        <div className={`mr-2 ${css(styles.sectionHeading)}`}>
          Biological Sex
        </div>
        {/* Gender */}
        <div className={css(styles.pressedBtnsContainer)}>
          {/* Male */}
          <div
            style={{ marginRight: '2px' }}
            className={css(styles.pressedBtn, gender === 'Male' && styles.activePressedBtn)}
            onClick={() => {
              if (!readOnlyMode) {
                handleOnChangeGender('Male');
              }
            }}
          >
            Male
          </div>
          {/* Female */}
          <div
            style={{ marginRight: '2px' }}
            className={css(styles.pressedBtn, gender === 'Female' && styles.activePressedBtn)}
            onClick={() => {
              if (!readOnlyMode) {
                handleOnChangeGender('Female');
              }
            }}
          >
            Female
          </div>
        </div>
      </div>
      {/* Search Patients */}
      <Button
        btnType='primary'
        isDisabled={!canSearchPatients || readOnlyMode}
        className='mt-4'
        style={{ width: '359px' }}
        onClick={handleOnClickSearchPatients}
      >
        Search Patients
      </Button>
      {/* ---------- Patient Card(s) ---------- */}
      <div style={{ width: '352px' }}>
        {patientSearchResults.map((el) => (
          <PatientCard
            key={el._id}
            patientData={el}
            onAddPatient={handleOnClickAddOrCreatePatient}
          />
        ))}
      </div>
    </div>
  );
};

export default AddPatient;
